import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import constants from '~/utils/constants';
import { addProcessIndicator } from '~/reducers/processIndicatorSlice';
import { MultiPlanEditApi } from '~/api/MultiPlanEditApi';
import { selectMainClient } from '~/reducers/mainClientSlice';
import { selectSchedulerId } from '~/reducers/webSolutionSlice';

import { REACT_QUERY_KEY as EDIT_HISTORY_QUERY_KEY } from '~/hooks/useGetEditHistory';
import { REACT_QUERY_KEY as EDIT_HISTORY_METRICS_QUERY_KEY } from '~/hooks/useGetEditRouteHistoryMetrics';

export const useUndoScheduleAction = () => {
    const { t } = useTranslation('translation');
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const mainClient = useSelector(selectMainClient);
    const schedulerId = useSelector(selectSchedulerId);
    const clientId = mainClient?.id;

    const { mutateAsync } = useMutation(
        (params: { clientId: string; schedulerId: string }) =>
            MultiPlanEditApi.undoScheduleAction(params),
        {
            onMutate: () => {
                dispatch(
                    addProcessIndicator({
                        message: t('UndoAction.inProgress'),
                        type: constants.processIndicator.UNDO,
                        inProgress: true,
                        error: false,
                        position: 'center'
                    })
                );
            },
            onSuccess: () => {
                dispatch(
                    addProcessIndicator({
                        message: t('UndoAction.done'),
                        type: constants.processIndicator.UNDO,
                        inProgress: false,
                        error: false,
                        position: 'center'
                    })
                );
                queryClient.invalidateQueries(EDIT_HISTORY_QUERY_KEY);
                queryClient.invalidateQueries(EDIT_HISTORY_METRICS_QUERY_KEY);
            },
            onError: (error) => {
                console.warn(error);
                dispatch(
                    addProcessIndicator({
                        message: t('UndoAction.inProgress'),
                        type: constants.processIndicator.UNDO,
                        inProgress: false,
                        error: true,
                        position: 'center'
                    })
                );
            }
        }
    );

    const undoScheduleAction = async () => {
        if (!clientId) {
            console.warn('Client id is missing');
            return;
        }

        if (!schedulerId) {
            console.warn('Scheduler id is missing');
            return;
        }

        try {
            await mutateAsync({ clientId, schedulerId });
        } catch {
            //
        }
    };

    return { undoScheduleAction };
};
